import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { api, clinicHeader } from "../api/client";
import { endSession, getWho } from "../api/session";
import { useSession } from "../useSession";

const ROLE_LABELS: Record<string, string> = {
  clinic_admin: "Clinic administrator",
  doctor: "Doctor",
  nurse: "Nurse",
  front_desk: "Front desk",
};

/** The destinations, in the order the mockup sets. */
const NAV = [
  { to: "/", label: "Dashboard", icon: "home", end: true },
  { to: "/queue", label: "In the clinic", icon: "clinic" },
  { to: "/patients", label: "Patients", icon: "person" },
  { to: "/messages", label: "Needs a reply", icon: "message" },
  { to: "/results", label: "Results waiting", icon: "flask" },
  { to: "/recalls", label: "Recalls", icon: "recall" },
  { to: "/appointments", label: "Appointments", icon: "calendar" },
  { to: "/calendar", label: "My calendar", icon: "calendar" },
  { to: "/attendance", label: "Attendance", icon: "chart" },
  // Last, and shown to everybody: the session does not carry the
  // org-admin flag, and the screen reads the server's refusal rather
  // than hiding a link and implying the rule is a preference.
  { to: "/organisation", label: "Organisation", icon: "clinic" },
];

/**
 * One line of a glyph each, drawn rather than fetched.
 *
 * An icon font or a sprite sheet would be two more things that can fail to
 * load, and a navigation whose icons have not arrived reads as a list of
 * nothing. These are stroked paths on a 24 box, which is all the mockup asks
 * for.
 */
const ICONS: Record<string, string> = {
  home: "M4 11 12 4l8 7M6 10v9h12v-9",
  clinic: "M4 20V9l8-5 8 5v11M9 20v-6h6v6M12 7v3M10.5 8.5h3",
  person: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 20c0-3.3 3.6-5 8-5s8 1.7 8 5",
  message: "M4 5h16v10H9l-5 4z",
  flask: "M9 3v6l-4.5 9A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.8-3L15 9V3M9 3h6M7.5 15h9",
  recall: "M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4",
  calendar: "M4 7h16v13H4zM4 7V5h16v2M9 3v4M15 3v4M8 12h3M8 16h3",
  chart: "M4 20h16M7 20v-7M12 20V6M17 20v-4",
  batch: "M7 3h10v4H7zM9 7v13h6V7M11 11h2M11 15h2",
};

function Icon({ name }: { name: string }) {
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * The frame every signed-in screen sits in.
 *
 * A fixed sidebar rather than a row of links across the top. The portal has
 * ten destinations and they did not fit on one line: the old header wrapped,
 * which put "Attendance" on a line of its own and made the clinic and the role
 * look like part of the navigation. A column has room for ten and room for the
 * ten after that.
 *
 * The clinic sits at the foot of it, where a switcher belongs, and stays on
 * screen. A person can work at two clinics with a different role at each
 * (ADR-010), so what they may do changes with it and a refusal only makes
 * sense if the context that caused it is visible.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { clinic } = useSession();
  const navigate = useNavigate();
  const role = clinic ? (ROLE_LABELS[clinic.role] ?? clinic.role) : "";
  const who = getWho();

  /* The counts beside Messages and Results waiting. The mockup carries them
     and they are the reason a worklist gets opened at all: a number nobody can
     see from the navigation is a queue nobody walks past.

     Refused for a role that may not read them, which is the rule rather than
     a fault, and a refusal simply leaves the badge off. */
  const [badges, setBadges] = useState<{ messages: number; results: number }>({
    messages: 0,
    results: 0,
  });

  useEffect(() => {
    let alive = true;
    const read = async () => {
      const header = clinicHeader();
      const [e, r] = await Promise.all([
        api.GET("/portal/escalations", { params: { header } }),
        api.GET("/portal/results/unacknowledged", { params: { header } }),
      ]);
      if (!alive) return;
      setBadges({
        messages: ((e.data ?? []) as unknown as unknown[]).length,
        results: (r.data?.items ?? []).length,
      });
    };
    void read();
    const timer = setInterval(() => void read(), 60_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [clinic?.id]);

  const countFor = (to: string) =>
    to === "/messages" ? badges.messages : to === "/results" ? badges.results : 0;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-panel">
        <Link to="/" className="sidebar-brand">
          <span className="sidebar-wordmark">Healtrics</span>
          <span className="sidebar-sub">Clinic portal</span>
        </Link>

        <nav className="sidebar-nav" aria-label="Main">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? "nav-item here" : "nav-item")}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {countFor(item.to) > 0 && <span className="badge">{countFor(item.to)}</span>}
            </NavLink>
          ))}
        </nav>
        </div>

        {clinic && (
          <div className="sidebar-clinic">
            <span className="sidebar-clinic-mark" aria-hidden="true">
              {initials(clinic.name)}
            </span>
            <span className="sidebar-clinic-words">
              <span className="sidebar-clinic-name">{clinic.name}</span>
              <span className="sidebar-clinic-role">{role}</span>
            </span>
          </div>
        )}
      </aside>

      <div className="frame">
        <header className="topbar">
          {/* The search the mockup puts across the top. It goes to the patient
              list, which is the screen that can actually answer it: four ways
              in, because the desk may only know one of them. */}
          <form
            className="topsearch"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              const q = new FormData(event.currentTarget).get("q");
              navigate(`/patients?q=${encodeURIComponent(String(q ?? ""))}`);
            }}
          >
            <svg className="topsearch-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3" />
            </svg>
            <input name="q" type="search" placeholder="Search patient name, IC, MRN or email" />
          </form>

          <Link to="/messages" className="topbell" aria-label="Patient messages needing a reply">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M13.7 20a2 2 0 0 1-3.4 0" />
            </svg>
            {badges.messages > 0 && <span className="dot" />}
          </Link>

          {/* The clinic is not the person.

              This fell back to the clinic's name and initials when the
              session had no name of its own, which printed it a third time:
              the greeting carries it, the clinic card at the foot of the
              sidebar carries it, and this put the same two letters beside
              the same words again, as though the building were signed in.
              A session that signed in before `full_name` was returned has
              only the clinic, and the honest answer there is the role,
              which is true either way and is what governs what may be
              opened (ADR-010). */}
          <div className="topbar-who">
            {who && <span className="avatar" aria-hidden="true">{initials(who)}</span>}
            <span className="topbar-words">
              {who && <span className="topbar-name">{who}</span>}
              <span className="topbar-role">{role}</span>
            </span>
          </div>

          <button type="button" className="link" onClick={endSession}>
            Sign out
          </button>
        </header>

        <main className="page">{children}</main>
      </div>
    </div>
  );
}
